import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDataHealth } from '../hooks/useAnalyticsV2';
import { Tooltip } from './ui/Tooltip';

/**
 * "Is my data arriving?" — the question nothing in the product used to answer.
 *
 * The three most common failure modes — an integration that was never really
 * connected, a provider that stopped sending, and a login not linked to an agent
 * — all used to render identically: a calm dashboard of zeros with a green tick.
 * Meanwhile the header carried a red "Worker Stopped" alarm driven by a Docker
 * container state, shown to every brand-new tenant, that linked to nothing.
 *
 * This pill reports the one thing a business owner can act on, in their own
 * words, and it always links somewhere useful.
 */

const STATES = {
  receiving: {
    tone: 'text-good-text bg-good-tint border-good-line',
    dot: 'bg-good-fill',
    label: 'Receiving calls',
    to: '/settings/integrations',
  },
  quiet: {
    tone: 'text-ink-700 bg-surface-2 border-line',
    dot: 'bg-slate-fill',
    label: 'No recent calls',
    to: '/settings/integrations',
  },
  stalled: {
    tone: 'text-bad-text bg-bad-tint border-bad-line',
    dot: 'bg-bad-fill',
    label: 'Not receiving calls',
    to: '/settings/integrations',
  },
  never: {
    tone: 'text-warn-text bg-warn-tint border-warn-line',
    dot: 'bg-warn-fill',
    label: 'Waiting for first call',
    to: '/settings/integrations',
  },
  unmapped_agent: {
    tone: 'text-warn-text bg-warn-tint border-warn-line',
    dot: 'bg-warn-fill',
    label: 'Account not linked',
    to: '/team',
  },
};

export default function DataHealthPill({ compact = false }) {
  const { data, isPending, isError } = useDataHealth();
  const { isOwner } = useAuth();

  // While we do not know, say we do not know. A green tick here would be exactly
  // the reassurance-by-default this component exists to remove.
  if (isPending) {
    return (
      <span className="inline-flex items-center gap-2 rounded-md border border-line bg-surface-2 px-2.5 py-1.5 text-micro text-ink-500">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-light" aria-hidden="true" />
        Checking data…
      </span>
    );
  }

  if (isError || !data) {
    return (
      <Tooltip content="We could not check whether calls are arriving. This is about our status check, not necessarily your integration.">
        <span className="inline-flex items-center gap-2 rounded-md border border-line bg-surface-2 px-2.5 py-1.5 text-micro text-ink-700">
          <span className="h-1.5 w-1.5 rounded-full bg-slate-fill" aria-hidden="true" />
          Status unknown
        </span>
      </Tooltip>
    );
  }

  const state = STATES[data.state] || STATES.quiet;
  const detail = [data.state_reason, data.last_call_local ? `Last call ${data.last_call_local}` : null]
    .filter(Boolean)
    .join(' · ');

  const body = (
    <>
      <span className={`h-1.5 w-1.5 rounded-full ${state.dot}`} aria-hidden="true" />
      <span>{state.label}</span>
      {!compact && data.last_call_local ? (
        <span className="hidden font-mono text-ink-500 md:inline">· {data.last_call_local}</span>
      ) : null}
    </>
  );
  const shape = `inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-micro font-medium ${state.tone}`;

  // Both destinations are owner-only pages. An agent still gets the status, but
  // not a link that ends on "Only owners can open this page".
  return (
    <Tooltip content={detail || state.label}>
      {isOwner ? (
        <Link
          to={state.to}
          className={`${shape} transition-colors duration-fast hover:brightness-[0.98]`}
        >
          {body}
        </Link>
      ) : (
        <span className={shape}>{body}</span>
      )}
    </Tooltip>
  );
}
